import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft,
  ArrowDown,
  Brain, 
  Sparkles, 
  Target, 
  Send, 
  Paperclip, 
  X,
  Zap,
  BarChart3,
  Clock,
  TrendingUp,
  Eye,
  RefreshCw,
  CheckCircle,
  ThumbsUp,
  ThumbsDown,
  Edit,
  File,
  Users,
  Mail,
  MessageSquare,
  Bot,
  User,
  Calendar,
  Settings
} from 'lucide-react';

interface RevolutionaryConversationViewProps {
  conversationId: string;
  onBack?: () => void;
}

const RevolutionaryConversationView: React.FC<RevolutionaryConversationViewProps> = ({
  conversationId,
  onBack
}) => {
  const [loading, setLoading] = useState(true);
  const [conversation, setConversation] = useState<any>(null);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [showPurposeDialog, setShowPurposeDialog] = useState(false);
  const [showWorkflowDetails, setShowWorkflowDetails] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<any>(null);
  const [replyContent, setReplyContent] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [generatingResponse, setGeneratingResponse] = useState(false);
  const [generatedResponse, setGeneratedResponse] = useState<any>(null);
  const [purposeType, setPurposeType] = useState('');
  const [purposeDescription, setPurposeDescription] = useState('');
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiInsights, setAiInsights] = useState<any>(null);
  
  // Scroll functionality
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(100);

  useEffect(() => {
    loadConversationDetails();
  }, [conversationId]);

  const loadConversationDetails = async () => {
    try {
      setLoading(true);
      
      // Real API call to get conversation details
      const response = await fetch(`http://localhost:3001/api/conversations/revolutionary/${conversationId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load conversation: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Loaded conversation data:', data);
      
      if (data.success && data.data) {
        setConversation(data.data);
        
        // Auto-populate reply subject with the original email subject
        if (data.data.messages && data.data.messages.length > 0) {
          const firstMessage = data.data.messages.find((msg: any) => msg.direction === 'outbound');
          if (firstMessage && firstMessage.subject) {
            const originalSubject = firstMessage.subject;
            const replySubject = originalSubject.startsWith('Re:') ? originalSubject : `Re: ${originalSubject}`;
            setReplySubject(replySubject);
          }
        }
        
        // Load real-time AI analysis
        await loadAIAnalysis();
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Failed to load revolutionary conversation:', error);
      
      // Show user-friendly error message
      const errorMessage = error instanceof Error ? error.message : 'Failed to load conversation details';
      setConversation({
        error: true,
        errorMessage,
        id: conversationId
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAIAnalysis = async () => {
    try {
      setAiAnalyzing(true);
      
      // Call real-time AI analysis endpoint
      const response = await fetch(`http://localhost:3001/api/conversations/revolutionary/${conversationId}/ai/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to analyze conversation: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('AI Analysis completed:', data);
      
      if (data.success && data.data) {
        setAiInsights(data.data);
      } else {
        throw new Error('Invalid AI analysis response');
      }
    } catch (error) {
      console.error('Failed to load AI analysis:', error);
      // Set fallback insights on error
      setAiInsights({
        sentiment: { overall: 'neutral', confidence: 50 },
        intent: { primary: 'general_inquiry', confidence: 50 },
        urgency: 'medium',
        nextBestAction: 'Continue conversation and provide helpful information',
        keyTopics: [],
        traceId: 'fallback'
      });
    } finally {
      setAiAnalyzing(false);
    }
  };

  const handleGenerateAIResponse = async () => {
    setGeneratingResponse(true);
    
    try {
      // Real API call to generate AI response
      const response = await fetch(`http://localhost:3001/api/conversations/revolutionary/${conversationId}/ai/generate-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          context: conversation?.purpose || {},
          preferences: {
            tone: 'professional',
            style: 'consultative',
            length: 'medium',
            include_cta: true
          },
          conversation_history: conversation?.messages || [],
          business_context: {
            industry: conversation?.contact?.company || 'Technology',
            stage: 'qualification',
            priority: conversation?.priority || 'medium'
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to generate AI response: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Generated AI response:', data);
      
      if (data.success && data.data) {
        setGeneratedResponse(data.data);
        setReplyContent(data.data.content || data.data.response);
      } else {
        throw new Error('Invalid response format from AI service');
      }
    } catch (error) {
      console.error('Failed to generate AI response:', error);
      
      // Show error to user instead of fallback
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate AI response';
      alert(`AI Response Generation Failed: ${errorMessage}`);
    } finally {
      setGeneratingResponse(false);
      setShowAIDialog(false); // Close modal after generation (success or failure)
    }
  };

  // Scroll to bottom functionality
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ 
      behavior: 'instant',
      block: 'end'
    });
  };

  // Start conversations at the bottom without jarring animation
  useEffect(() => {
    if (conversation?.messages?.length) {
      const container = messagesContainerRef.current;
      if (container) {
        // Use requestAnimationFrame to ensure DOM is fully rendered
        requestAnimationFrame(() => {
          // Always start at the bottom for a consistent experience
          container.scrollTop = container.scrollHeight;
        });
      }
    }
  }, [conversation?.id]); // Only run when conversation changes, not on every message

  // Auto-scroll when AI generates a response (since user triggered it)
  useEffect(() => {
    if (generatedResponse) {
      setTimeout(scrollToBottom, 200);
    }
  }, [generatedResponse]);

  // Auto-scroll when user is already near bottom and new messages arrive
  const handleNewMessage = () => {
    const container = messagesContainerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      
      if (isNearBottom) {
        setTimeout(scrollToBottom, 100);
      }
    }
  };

  // Handle scroll events to show/hide scroll button and track progress
  useEffect(() => {
    const handleScroll = () => {
      const container = messagesContainerRef.current;
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const isScrolledUp = scrollHeight - scrollTop - clientHeight > 100;
        setShowScrollButton(isScrolledUp);
        
        // Calculate scroll progress (0-100%)
        const maxScroll = scrollHeight - clientHeight;
        const progress = maxScroll > 0 ? Math.round((scrollTop / maxScroll) * 100) : 100;
        setScrollProgress(progress);
      }
    };

    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [conversation]);

  // Enhanced scroll to bottom with force option
  const scrollToBottomForced = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  // Keyboard shortcuts for scrolling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + End = scroll to bottom
      if ((e.metaKey || e.ctrlKey) && e.key === 'End') {
        e.preventDefault();
        scrollToBottomForced();
      }
      // Cmd/Ctrl + Home = scroll to top
      if ((e.metaKey || e.ctrlKey) && e.key === 'Home') {
        e.preventDefault();
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTop = 0;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSendReply = async () => {
    if (!replyContent.trim()) return;
    
    try {
      const response = await fetch(`http://localhost:3001/api/conversations/revolutionary/${conversationId}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: replyContent,
          subject: replySubject || 'Re: Conversation',
          direction: 'outbound',
          source: 'human'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to send reply: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Reply sent:', data);
      
      // Clear the form
      setReplyContent('');
      setReplySubject('');
      
      // Reload conversation to show the new message
      await loadConversationDetails();

      // Scroll to bottom to show new message
      setTimeout(() => {
        scrollToBottomForced();
      }, 200);
      
    } catch (error) {
      console.error('Failed to send reply:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send reply';
      alert(`Send Reply Failed: ${errorMessage}`);
    }
  };

  const handleSetPurpose = async () => {
    if (!purposeType.trim() || !purposeDescription.trim()) {
      alert('Please fill in both purpose type and description');
      return;
    }
    
    try {
      const response = await fetch(`http://localhost:3001/api/conversations/revolutionary/${conversationId}/purpose`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          purpose_type: purposeType,
          description: purposeDescription,
          target_outcome: `Achieve ${purposeType} objectives through strategic conversation management`
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to set purpose: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Purpose set:', data);
      
      // Clear the form and close modal
      setPurposeType('');
      setPurposeDescription('');
      setShowPurposeDialog(false);
      
      // Reload conversation to show updated purpose
      await loadConversationDetails();
      
    } catch (error) {
      console.error('Failed to set purpose:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to set conversation purpose';
      alert(`Set Purpose Failed: ${errorMessage}`);
    }
  };

  const handleViewWorkflowDetails = (workflow: any) => {
    setSelectedWorkflow(workflow);
    setShowWorkflowDetails(true);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400 text-lg">Loading revolutionary conversation...</p>
        </div>
      </div>
    );
  }

  // Show error state if conversation failed to load
  if (conversation?.error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Failed to Load Conversation</h2>
          <p className="text-neutral-400 mb-6">{conversation.errorMessage}</p>
          <div className="space-y-3">
            <button
              onClick={loadConversationDetails}
              className="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
            {onBack && (
              <button
                onClick={onBack}
                className="w-full px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition-colors"
              >
                Go Back
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-indigo-500/5 to-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative flex-1 flex flex-col min-h-0">
        {/* Enhanced Header - Compact for laptops */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 border-b border-neutral-700/50 bg-gradient-to-r from-neutral-900/90 to-neutral-800/60 backdrop-blur-xl flex-shrink-0"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-4">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-2 rounded-lg bg-neutral-800/50 hover:bg-neutral-700/50 text-neutral-300 hover:text-white transition-all duration-200 border border-neutral-700/50"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white text-lg font-bold">AV</span>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  Revolutionary Conversation
                </h1>
                <div className="flex items-center space-x-3 mt-1">
                  <p className="text-lg text-white font-medium">{conversation?.contact?.name}</p>
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full border border-blue-500/30">
                    {conversation?.contact?.company}
                  </span>
                </div>
                <p className="text-neutral-400 text-sm">{conversation?.contact?.email}</p>
              </div>
            </div>

            {/* Status and Priority Badges */}
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                <div className="w-2 h-2 rounded-full bg-green-400 mr-1" />
                Active
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium bg-red-500/20 text-red-300 border border-red-500/30">
                High Priority
              </span>
            </div>
          </div>

          {/* Action Buttons - Compact */}
          <div className="flex items-center justify-between">
            <div className="flex space-x-2">
              <button 
                onClick={() => setShowPurposeDialog(true)}
                className="inline-flex items-center px-3 py-2 rounded-lg bg-neutral-800/50 hover:bg-neutral-700/50 text-neutral-300 hover:text-white transition-all duration-200 border border-neutral-700/50 text-sm"
              >
                <Target className="w-4 h-4 mr-1" />
                Purpose
              </button>
              
              <button 
                onClick={() => setShowAIDialog(true)}
                className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white transition-all duration-300 shadow-lg hover:shadow-xl text-sm"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                AI Response
              </button>
              
              <button className="inline-flex items-center px-3 py-2 rounded-lg bg-neutral-800/50 hover:bg-neutral-700/50 text-neutral-300 hover:text-white transition-all duration-200 border border-neutral-700/50 text-sm">
                <BarChart3 className="w-4 h-4 mr-1" />
                Analytics
              </button>

              <button className="inline-flex items-center px-3 py-2 rounded-lg bg-neutral-800/50 hover:bg-neutral-700/50 text-neutral-300 hover:text-white transition-all duration-200 border border-neutral-700/50 text-sm">
                <Settings className="w-4 h-4 mr-1" />
                Settings
              </button>
            </div>

            <div className="flex items-center space-x-2 text-xs text-neutral-400">
              <Clock className="w-3 h-3" />
              <span>2m ago</span>
            </div>
          </div>
        </motion.div>

        {/* Main Content Grid - Compact for laptops */}
        <div className="flex-1 grid grid-cols-12 gap-4 p-4 min-h-0 h-0">
          {/* Messages Area - Takes up most space */}
          <div className="col-span-8 flex flex-col bg-gradient-to-br from-neutral-900/60 to-neutral-800/40 backdrop-blur-xl border border-neutral-700/50 rounded-2xl shadow-xl overflow-hidden min-h-0">
            {/* Messages Header - Compact */}
            <div className="p-4 border-b border-neutral-700/50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Messages</h3>
                <div className="flex items-center space-x-3 text-xs text-neutral-400">
                  <span className="flex items-center">
                    <MessageSquare className="w-3 h-3 mr-1" />
                    {conversation?.messages?.length || 0}
                  </span>
                  <span className="flex items-center">
                    <Users className="w-3 h-3 mr-1" />
                    2
                  </span>
                  {scrollProgress < 95 && (
                    <div className="flex items-center space-x-1">
                      <span>{scrollProgress}%</span>
                      <div className="w-8 h-1 bg-neutral-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 transition-all duration-200"
                          style={{ width: `${scrollProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

      {/* Messages */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4 scroll-smooth scrollbar-thin scrollbar-track-neutral-800 scrollbar-thumb-neutral-600 hover:scrollbar-thumb-neutral-500 min-h-0"
              style={{ 
                scrollbarWidth: 'thin',
                scrollbarColor: '#525252 #262626',
                maxHeight: '100%'
              }}
            >
              {conversation?.messages?.map((message: any, index: number) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-2xl px-6 py-4 rounded-2xl shadow-lg ${
                    message.direction === 'outbound'
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                      : 'bg-gradient-to-r from-neutral-800 to-neutral-700 text-white border border-neutral-600/50'
                  }`}>
                    <div className="flex items-start space-x-3">
                      {message.direction === 'inbound' ? (
                        <User className="w-5 h-5 mt-1 text-neutral-400 flex-shrink-0" />
                      ) : message.source === 'workflow' ? (
                        <Zap className="w-5 h-5 mt-1 text-purple-200 flex-shrink-0" />
                      ) : (
                        <Bot className="w-5 h-5 mt-1 text-blue-200 flex-shrink-0" />
                      )}
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={`text-sm ${
                            message.direction === 'outbound' ? 'text-blue-100' : 'text-neutral-400'
                          }`}>
                        {new Date(message.timestamp).toLocaleString()}
                          </span>
                          
                          {message.source && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              message.source === 'ai' ? 'bg-blue-500/30 text-blue-200' :
                              message.source === 'workflow' ? 'bg-purple-500/30 text-purple-200' :
                              'bg-neutral-600/30 text-neutral-300'
                            }`}>
                              {message.source}
                            </span>
                          )}
                        </div>
                        
                        {/* Message Content Processing */}
                        {(() => {
                          // Clean message content extraction
                          let messageContent = message.content;
                          let messageSource = message.source || (message.direction === 'inbound' ? 'customer' : 'team');
                          let isWorkflowMessage = message.message_type === 'workflow' || message.source === 'workflow';
                          
                          // Extract clean content from email replies that might have quoted text
                          if (message.direction === 'inbound' && messageContent.includes('On ') && messageContent.includes(' wrote:')) {
                            // Extract the reply content before the quoted section
                            const parts = messageContent.split(/On .+ wrote:/);
                            messageContent = parts[0].trim();
                          }
                          
                          // Handle workflow messages more cleanly
                          if (isWorkflowMessage && messageContent.includes('Email sent via workflow:')) {
                            const workflowMatch = messageContent.match(/Email sent via workflow: (.+?)(?:\s-\s(.+))?$/);
                            if (workflowMatch && workflowMatch[2]) {
                              messageContent = workflowMatch[2];
                            }
                          }
                          
                          return (
                            <div className="space-y-3">
                              {/* Message Content */}
                              <div className="space-y-2">
                                {message.subject && message.subject !== 'Re: Test Subject' && message.subject !== 'Test Subject' && (
                                  <div className={`text-sm font-medium ${
                                    message.direction === 'outbound' ? 'text-blue-100' : 'text-neutral-300'
                                  }`}>
                                    {message.subject}
                                  </div>
                                )}
                                
                                <p className="text-base leading-relaxed whitespace-pre-wrap">
                                  {messageContent}
                                </p>
                              </div>
                              
                              {/* Message Metadata - Compact and Clean */}
                              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                                <div className="flex items-center space-x-2">
                                  {/* Source Badge */}
                                  {isWorkflowMessage && (
                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                      <Zap className="w-3 h-3 mr-1" />
                                      Workflow
                                    </span>
                                  )}
                                  
                                  {message.direction === 'inbound' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                                      📨 Reply
                                    </span>
                                  )}
                                  
                                  {/* Delivery Status */}
                                  {message.metadata?.sendgridMessageId && (
                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                                      ✓ Delivered
                                    </span>
                                  )}
                                </div>
                                
                                {/* Timestamp */}
                                <span className={`text-xs ${
                                  message.direction === 'outbound' ? 'text-blue-200' : 'text-neutral-400'
                                }`}>
                                  {new Date(message.timestamp).toLocaleTimeString([], { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </span>
                              </div>
                              
                              {/* Attachments */}
                              {message.attachments && message.attachments.length > 0 && (
                                <div className="flex items-center space-x-2 pt-2">
                                  <Paperclip className="w-4 h-4 text-neutral-400" />
                                  <span className="text-sm text-neutral-400">
                                    {message.attachments.length} attachment{message.attachments.length > 1 ? 's' : ''}
                                  </span>
                                </div>
                              )}
                              
                              {/* Workflow Details Button */}
                              {isWorkflowMessage && (message.workflowExecutionId || message.workflow_id) && (
                                <button 
                                  onClick={() => {
                                    const workflow = conversation?.workflowExecutions?.find((w: any) => 
                                      w.id === message.workflowExecutionId || w.workflow_id === message.workflow_id
                                    );
                                    if (workflow) {
                                      handleViewWorkflowDetails(workflow);
                                    }
                                  }}
                                  className="inline-flex items-center px-2 py-1 rounded-lg text-xs hover:bg-white/10 text-neutral-300 hover:text-white transition-all"
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  View Workflow Details
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Ultra-Sleek AI Analysis */}
              {aiInsights && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex justify-center mt-3"
                >
                  <div className="max-w-lg w-full">
                    {/* Ultra-Sleek AI Insights */}
                    <div className="bg-white/[0.01] border border-white/[0.06] rounded-xl overflow-hidden">
                      <div className="px-4 py-3 border-b border-white/[0.05]">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className={`w-2 h-2 rounded-full ${aiAnalyzing ? 'bg-yellow-400 animate-pulse' : 'bg-blue-400'}`}></div>
                            <span className="text-white/90 font-medium">AI Insights</span>
                            {aiAnalyzing && <span className="text-white/50 text-xs">Analyzing...</span>}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="px-2 py-0.5 bg-white/[0.05] text-white/60 text-xs rounded">
                              {aiInsights?.sentiment?.confidence ? `${Math.round(aiInsights.sentiment.confidence)}%` : '85%'}
                            </span>
                            <button 
                              onClick={() => loadAIAnalysis()}
                              disabled={aiAnalyzing}
                              className="p-1 text-white/40 hover:text-white/70 transition-colors"
                              title="Refresh AI Analysis"
                            >
                              <RefreshCw className={`w-3 h-3 ${aiAnalyzing ? 'animate-spin' : ''}`} />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4 space-y-4">
                        {/* Sentiment */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-white/60 text-sm">Sentiment</span>
                            <span className="text-white/90 text-sm font-medium capitalize">
                              {aiInsights?.sentiment?.overall || 'Positive'}
                            </span>
                          </div>
                          <div className="w-full bg-white/[0.05] rounded-full h-1">
                            <div className="bg-green-400 h-1 rounded-full transition-all duration-1000" 
                                 style={{ width: `${aiInsights?.sentiment?.confidence || 85}%` }}></div>
                          </div>
                        </div>

                        {/* Intent */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-white/60 text-sm">Intent</span>
                            <span className="text-white/90 text-sm font-medium capitalize">
                              {aiInsights?.intent?.primary?.replace('_', ' ') || 'Product Interest'}
                            </span>
                          </div>
                          <div className="w-full bg-white/[0.05] rounded-full h-1">
                            <div className="bg-purple-400 h-1 rounded-full transition-all duration-1000" 
                                 style={{ width: `${aiInsights?.intent?.confidence || 78}%` }}></div>
                          </div>
                        </div>

                        {/* Priority */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-white/60 text-sm">Priority</span>
                            <span className="text-white/90 text-sm font-medium capitalize">
                              {aiInsights?.urgency || 'Medium'}
                            </span>
                          </div>
                          <div className="w-full bg-white/[0.05] rounded-full h-1">
                            <div className={`h-1 rounded-full transition-all duration-1000 ${
                              aiInsights?.urgency === 'high' ? 'bg-red-400 w-full' :
                              aiInsights?.urgency === 'medium' ? 'bg-orange-400 w-3/4' :
                              'bg-yellow-400 w-1/2'
                            }`}></div>
                          </div>
                        </div>

                        {/* Next Action */}
                        <div className="pt-2 border-t border-white/[0.05]">
                          <span className="text-white/60 text-sm block mb-2">Recommended Action</span>
                          <p className="text-white/85 text-sm leading-relaxed">
                            {aiInsights?.nextBestAction || 'Continue conversation and provide helpful information'}
                          </p>
                        </div>

                        {/* Key Topics */}
                        {aiInsights?.keyTopics && aiInsights.keyTopics.length > 0 && (
                          <div className="pt-2 border-t border-white/[0.05]">
                            <span className="text-white/60 text-sm block mb-2">Topics</span>
                            <div className="flex flex-wrap gap-1">
                              {aiInsights.keyTopics.map((topic: string, index: number) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-white/[0.05] text-white/70 text-xs rounded-md"
                                >
                                  {topic}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Ultra-Sleek AI Response Generation */}
              {generatedResponse ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-center mt-3"
                >
                  <div className="max-w-lg w-full bg-white/[0.02] border border-white/[0.08] rounded-xl overflow-hidden">
                    <div className="px-3 py-2 border-b border-white/[0.05] flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                        <span className="text-white/70 text-xs font-medium">AI Suggestion</span>
                        <span className="px-1.5 py-0.5 bg-white/[0.05] text-white/60 text-xs rounded">
                          {generatedResponse.confidence || 85}%
                        </span>
                      </div>
                      <button onClick={() => setGeneratedResponse(null)} className="text-white/40 hover:text-white/60">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    
                    <div className="p-3">
                      <div className="bg-white/[0.02] rounded-lg p-3 mb-3 border border-white/[0.05]">
                        <p className="text-white/85 text-sm leading-relaxed">
                          {generatedResponse.content}
                        </p>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={async () => {
                            setReplyContent(generatedResponse.content);
                            setGeneratedResponse(null);
                            
                            // Auto-send the AI response
                            try {
                              const response = await fetch(`http://localhost:3001/api/conversations/revolutionary/${conversationId}/reply`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                  content: generatedResponse.content,
                                  subject: replySubject || 'Re: Conversation',
                                  direction: 'outbound',
                                  source: 'ai'
                                })
                              });
              
                              if (!response.ok) {
                                throw new Error(`Failed to send AI response: ${response.status}`);
                              }
              
                              // Clear form and reload conversation
                              setReplyContent('');
                              await loadConversationDetails();
                              
                              // Scroll to show new message
                              setTimeout(() => {
                                scrollToBottomForced();
                              }, 200);
                              
                            } catch (error) {
                              console.error('Failed to send AI response:', error);
                              alert('Failed to send AI response automatically. Please try sending manually.');
                            }
                          }}
                          className="flex-1 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center space-x-1"
                        >
                          <CheckCircle className="w-3 h-3" />
                          <span>Send</span>
                        </button>
                        
                        <button
                          onClick={() => {
                            setReplyContent(generatedResponse.content);
                            setGeneratedResponse(null);
                          }}
                          className="px-3 py-1.5 bg-white/[0.05] hover:bg-white/[0.1] text-white/70 hover:text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center"
                        >
                          <Edit className="w-3 h-3" />
                        </button>
                        
                        <button
                          onClick={() => handleGenerateAIResponse()}
                          className="px-3 py-1.5 bg-white/[0.05] hover:bg-white/[0.1] text-white/70 hover:text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center"
                          disabled={generatingResponse}
                        >
                          <RefreshCw className={`w-3 h-3 ${generatingResponse ? 'animate-spin' : ''}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-center mt-3"
                >
                  <button
                    onClick={() => handleGenerateAIResponse()}
                    disabled={generatingResponse}
                    className="px-4 py-2 bg-white/[0.02] hover:bg-white/[0.04] border border-white/[0.08] hover:border-white/[0.12] text-white/70 hover:text-white/90 rounded-xl transition-all duration-200 flex items-center space-x-2 text-sm"
                  >
                    {generatingResponse ? (
                      <>
                        <div className="w-3 h-3 border border-white/20 border-t-white/60 rounded-full animate-spin"></div>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3" />
                        <span>Generate AI Response</span>
                      </>
                    )}
                  </button>
                </motion.div>
              )}

              {/* Scroll anchor point */}
              <div ref={messagesEndRef} className="h-1" />
            </div>

            {/* Scroll Navigation Buttons */}
            <AnimatePresence>
              {showScrollButton && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="absolute bottom-32 right-8 z-10 flex flex-col space-y-2"
                >
                  {/* Scroll to Top Button (only if scrolled significantly) */}
                  {scrollProgress > 20 && (
                    <button
                      onClick={() => {
                        const container = messagesContainerRef.current;
                        if (container) {
                          container.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                      className="p-2 bg-gradient-to-r from-neutral-700 to-neutral-600 hover:from-neutral-600 hover:to-neutral-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                      title="Scroll to top (⌘+Home)"
                    >
                      <ArrowDown className="w-3 h-3 rotate-180" />
                    </button>
                  )}
                  
                  {/* Scroll to Bottom Button */}
                  <button
                    onClick={scrollToBottomForced}
                    className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-2"
                    title="Scroll to bottom (⌘+End)"
                  >
                    <ArrowDown className="w-4 h-4" />
                    <span className="text-sm font-medium">Latest</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Enhanced Compose Area - Compact */}
            <div className="p-4 border-t border-neutral-700/50 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 flex-shrink-0">
              <div className="space-y-3">
                {/* Subject Line */}
                <input
                  type="text"
              value={replySubject}
              onChange={(e) => setReplySubject(e.target.value)}
                  placeholder="Subject line..."
                  className="w-full px-3 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent text-sm"
                />

                {/* Message Input */}
                <div className="flex items-start space-x-3">
                  <div className="flex-1">
                    <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Type your message..."
                      className="w-full px-3 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent resize-none text-sm"
                      rows={3}
                    />
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <button className="p-2 rounded-lg bg-neutral-800/50 hover:bg-neutral-700/50 text-neutral-300 hover:text-white transition-all duration-200 border border-neutral-700/50">
                      <Paperclip className="w-4 h-4" />
                    </button>
                    
                    <button 
                      disabled={!replyContent.trim()}
                      onClick={handleSendReply}
                      className="p-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Quick Actions - Compact */}
                <div className="flex items-center justify-between">
                  <div className="flex space-x-1">
                    <button className="px-2 py-1 bg-neutral-800/50 hover:bg-neutral-700/50 text-neutral-300 hover:text-white transition-all duration-200 border border-neutral-700/50 rounded text-xs">
                      📎 File
                    </button>
                    <button className="px-2 py-1 bg-neutral-800/50 hover:bg-neutral-700/50 text-neutral-300 hover:text-white transition-all duration-200 border border-neutral-700/50 rounded text-xs">
                      📷 Media
                    </button>
                    <button className="px-2 py-1 bg-neutral-800/50 hover:bg-neutral-700/50 text-neutral-300 hover:text-white transition-all duration-200 border border-neutral-700/50 rounded text-xs">
                      📋 Template
                    </button>
                  </div>
                  
                  <div className="text-xs text-neutral-500">
                    {replyContent.length}/1000
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar - AI Insights & Workflow Info - Compact */}
          <div className="col-span-4 space-y-3 overflow-y-auto min-h-0 scrollbar-thin scrollbar-track-neutral-800 scrollbar-thumb-neutral-600 hover:scrollbar-thumb-neutral-500"
               style={{ 
                 scrollbarWidth: 'thin',
                 scrollbarColor: '#525252 #262626'
               }}>
            {/* Detailed AI Insights */}
            <div className="bg-gradient-to-br from-neutral-900/70 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-xl shadow-xl overflow-hidden">
              <div className="px-4 py-3 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-b border-neutral-700/50">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-2">
                    <Brain className="w-3 h-3 text-white" />
                  </div>
                  AI Insights
                  <span className="ml-2 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                    {aiInsights?.sentiment?.confidence ? `${Math.round(aiInsights.sentiment.confidence)}%` : '75%'}
                  </span>
                </h3>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Sentiment Analysis */}
                <div className="bg-gradient-to-br from-green-500/5 to-emerald-500/5 border border-green-500/20 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-green-400 font-medium text-sm flex items-center">
                      <span className="text-lg mr-2">😊</span>
                      Sentiment Analysis
                    </span>
                    <span className="text-green-400 text-xs font-bold">
                      {aiInsights?.sentiment?.confidence ? `${Math.round(aiInsights.sentiment.confidence)}%` : '85%'}
                    </span>
                  </div>
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-neutral-400">Overall Tone</span>
                      <span className="text-white font-medium capitalize">
                        {aiInsights?.sentiment?.overall || 'Positive'}
                      </span>
                    </div>
                    <div className="w-full bg-neutral-800 rounded-full h-2">
                      <div className="bg-gradient-to-r from-green-500 to-emerald-400 h-2 rounded-full transition-all duration-1000" 
                           style={{ width: `${aiInsights?.sentiment?.confidence || 85}%` }}></div>
                    </div>
                  </div>
                  <p className="text-neutral-300 text-xs leading-relaxed">
                    Customer shows positive engagement with technical interest in platform capabilities.
                  </p>
                </div>

                {/* Intent & Context */}
                <div className="bg-gradient-to-br from-purple-500/5 to-violet-500/5 border border-purple-500/20 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-purple-400 font-medium text-sm flex items-center">
                      <Target className="w-4 h-4 mr-2" />
                      Intent & Context
                    </span>
                    <span className="text-purple-400 text-xs font-bold">
                      {aiInsights?.intent?.confidence ? `${Math.round(aiInsights.intent.confidence)}%` : '78%'}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-neutral-400 text-xs">Primary Intent</span>
                      <span className="text-white text-xs font-medium capitalize">
                        {aiInsights?.intent?.primary?.replace('_', ' ') || 'Product Interest'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-neutral-400 text-xs">Context</span>
                      <span className="text-white text-xs font-medium">Technical Evaluation</span>
                    </div>
                    <div className="w-full bg-neutral-800 rounded-full h-2">
                      <div className="bg-gradient-to-r from-purple-500 to-violet-400 h-2 rounded-full transition-all duration-1000" 
                           style={{ width: `${aiInsights?.intent?.confidence || 78}%` }}></div>
                    </div>
                  </div>
                </div>

                {/* Priority & Urgency */}
                <div className="bg-gradient-to-br from-orange-500/5 to-red-500/5 border border-orange-500/20 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-orange-400 font-medium text-sm flex items-center">
                      <Clock className="w-4 h-4 mr-2" />
                      Priority Assessment
                    </span>
                    <span className="text-orange-400 text-xs font-bold">Medium</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-neutral-400 text-xs">Urgency Level</span>
                      <span className="text-white text-xs font-medium capitalize">
                        {aiInsights?.urgency || 'Medium'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-neutral-400 text-xs">Response Window</span>
                      <span className="text-white text-xs font-medium">24 hours</span>
                    </div>
                    <div className="w-full bg-neutral-800 rounded-full h-2">
                      <div className={`h-2 rounded-full transition-all duration-1000 ${
                        aiInsights?.urgency === 'high' ? 'bg-gradient-to-r from-red-500 to-orange-400 w-full' :
                        aiInsights?.urgency === 'medium' ? 'bg-gradient-to-r from-orange-500 to-yellow-400 w-3/4' :
                        'bg-gradient-to-r from-yellow-500 to-green-400 w-1/2'
                      }`}></div>
                    </div>
                  </div>
                </div>

                {/* Strategic Recommendations */}
                <div className="bg-gradient-to-br from-blue-500/5 to-cyan-500/5 border border-blue-500/20 rounded-xl p-3">
                  <div className="flex items-center mb-2">
                    <Sparkles className="w-4 h-4 mr-2 text-blue-400" />
                    <span className="text-blue-400 font-medium text-sm">Strategic Recommendations</span>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-neutral-800/30 rounded-lg p-2">
                      <p className="text-white text-xs font-medium mb-1">Immediate Action</p>
                      <p className="text-neutral-300 text-xs leading-relaxed">
                        {aiInsights?.nextBestAction || 'Showcase platform features and schedule demo'}
                      </p>
                    </div>
                    <div className="bg-neutral-800/30 rounded-lg p-2">
                      <p className="text-white text-xs font-medium mb-1">Follow-up Strategy</p>
                      <p className="text-neutral-300 text-xs leading-relaxed">
                        Provide technical documentation and ROI case studies
                      </p>
                    </div>
                  </div>
                </div>

                {/* Key Topics & Entities */}
                {aiInsights?.keyTopics && aiInsights.keyTopics.length > 0 && (
                  <div className="bg-gradient-to-br from-indigo-500/5 to-purple-500/5 border border-indigo-500/20 rounded-xl p-3">
                    <div className="flex items-center mb-2">
                      <span className="text-indigo-400 font-medium text-sm">Key Topics</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {aiInsights.keyTopics.map((topic: string, index: number) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-500/20 text-indigo-300 border border-indigo-500/30"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Workflow Executions - Compact */}
            <div className="bg-gradient-to-br from-neutral-900/60 to-neutral-800/40 backdrop-blur-xl border border-neutral-700/50 rounded-xl shadow-xl p-4">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                <Zap className="w-5 h-5 mr-2 text-purple-400" />
                Workflow
              </h3>
              
              <div className="space-y-3">
                {conversation?.workflowExecutions?.slice(0, 2).map((execution: any, index: number) => (
                  <div
                    key={execution.id}
                    className="p-3 bg-neutral-800/30 border border-neutral-700/30 rounded-lg hover:bg-neutral-800/50 transition-all duration-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Zap className="w-4 h-4 text-purple-400" />
                        <span className="text-white font-medium text-sm">
                          {execution.metadata?.workflowName || 'New Workflow'}
                        </span>
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                        {execution.execution_status || 'Completed'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <div className="text-white font-medium">{execution.output_data?.stepResults?.[0]?.output?.recipients ? '1' : '1'}</div>
                        <div className="text-neutral-400">Emails</div>
                      </div>
                      <div className="text-center">
                        <div className="text-green-400 font-medium">
                          {Math.round((execution.output_data?.successfulSteps / execution.output_data?.totalSteps * 100) || 95)}%
                        </div>
                        <div className="text-neutral-400">Success</div>
                      </div>
                      <div className="text-center">
                        <div className="text-blue-400 font-medium">{execution.duration_ms || 0}ms</div>
                        <div className="text-neutral-400">Duration</div>
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => handleViewWorkflowDetails(execution)}
                      className="mt-2 w-full text-xs text-purple-400 hover:text-purple-300 transition-colors flex items-center justify-center"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View Details
                    </button>
                  </div>
                ))}
                
                {conversation?.workflowExecutions?.length > 2 && (
                  <div className="text-center">
                    <button className="text-xs text-neutral-400 hover:text-neutral-300 transition-colors">
                      +{conversation.workflowExecutions.length - 2} more executions
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Performance Metrics - Compact */}
            <div className="bg-gradient-to-br from-neutral-900/60 to-neutral-800/40 backdrop-blur-xl border border-neutral-700/50 rounded-xl shadow-xl p-4">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-green-400" />
                Performance
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-400">2.3m</div>
                  <div className="text-sm text-neutral-400">Response Time</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400">98%</div>
                  <div className="text-sm text-neutral-400">Delivery Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-400">4.8</div>
                  <div className="text-sm text-neutral-400">AI Confidence</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-400">85%</div>
                  <div className="text-sm text-neutral-400">Engagement</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Response Modal */}
      <AnimatePresence>
        {showAIDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShowAIDialog(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-br from-neutral-900 to-neutral-800 border border-neutral-700 rounded-2xl shadow-2xl max-w-4xl w-full mx-8 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-neutral-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-semibold text-white flex items-center">
                    <Sparkles className="w-7 h-7 mr-3 text-blue-400" />
                    AI Response Generator
                  </h3>
                  <button
                    onClick={() => setShowAIDialog(false)}
                    className="p-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-300 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-semibold text-white mb-2">AI Response Generation</h4>
                  <p className="text-neutral-400 max-w-md mx-auto">
                    Advanced AI-powered response generation with context awareness, sentiment analysis, and personalization.
                  </p>
                  <button 
                    onClick={handleGenerateAIResponse}
                    disabled={generatingResponse}
                    className="mt-6 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generatingResponse ? (
                      <div className="flex items-center">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                        Generating...
                      </div>
                    ) : (
                      'Generate Response'
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Purpose Setting Modal */}
      <AnimatePresence>
        {showPurposeDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShowPurposeDialog(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-br from-neutral-900 to-neutral-800 border border-neutral-700 rounded-2xl shadow-2xl max-w-2xl w-full mx-8 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-neutral-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-semibold text-white flex items-center">
                    <Target className="w-7 h-7 mr-3 text-blue-400" />
                    Set Conversation Purpose
                  </h3>
                  <button
                    onClick={() => setShowPurposeDialog(false)}
                    className="p-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-300 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Target className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-xl font-semibold text-white mb-2">Set Conversation Purpose</h4>
                    <p className="text-neutral-400">
                      Define the purpose and goals for this conversation to enable AI-powered suggestions.
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-300 mb-2">Purpose Type</label>
                      <select
              value={purposeType}
                        onChange={(e) => setPurposeType(e.target.value)}
                        className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      >
                        <option value="">Select purpose type...</option>
                        <option value="sales">Sales & Lead Generation</option>
                        <option value="support">Customer Support</option>
                        <option value="onboarding">Customer Onboarding</option>
                        <option value="retention">Customer Retention</option>
                        <option value="feedback">Feedback Collection</option>
                        <option value="education">Product Education</option>
                        <option value="demo">Product Demonstration</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-neutral-300 mb-2">Description</label>
                      <textarea
            value={purposeDescription}
            onChange={(e) => setPurposeDescription(e.target.value)}
                        placeholder="Describe the specific goals and outcomes for this conversation..."
                        className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
                        rows={3}
                      />
                    </div>
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowPurposeDialog(false)}
                      className="flex-1 px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition-colors"
                    >
            Cancel
                    </button>
                    <button
                      onClick={handleSetPurpose}
                      disabled={!purposeType || !purposeDescription.trim()}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Set Purpose
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Workflow Details Modal */}
      <AnimatePresence>
        {showWorkflowDetails && selectedWorkflow && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShowWorkflowDetails(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-br from-neutral-900 to-neutral-800 border border-neutral-700 rounded-2xl shadow-2xl max-w-4xl w-full mx-8 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-neutral-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-semibold text-white flex items-center">
                    <Zap className="w-7 h-7 mr-3 text-purple-400" />
                    Workflow Execution Details
                  </h3>
                  <button
                    onClick={() => setShowWorkflowDetails(false)}
                    className="p-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-300 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 max-h-96 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-400 mb-1">Execution ID</label>
                      <p className="text-white font-mono text-sm bg-neutral-800/50 px-3 py-2 rounded-lg border border-neutral-700/50">
                        {selectedWorkflow.id}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-neutral-400 mb-1">Status</label>
                      <span className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {selectedWorkflow.execution_status}
                      </span>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-neutral-400 mb-1">Trace ID</label>
                      <p className="text-white font-mono text-sm bg-neutral-800/50 px-3 py-2 rounded-lg border border-neutral-700/50">
                        {selectedWorkflow.trace_id}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-neutral-400 mb-1">Created</label>
                      <p className="text-neutral-300 text-sm">
                        {new Date(selectedWorkflow.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-400 mb-2">Performance Metrics</label>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-neutral-300">Success Rate</span>
                          <span className="text-green-400 font-medium">
                            {selectedWorkflow.performance_metrics?.success_rate || 0}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-neutral-300">Delivery Rate</span>
                          <span className="text-green-400 font-medium">
                            {selectedWorkflow.performance_metrics?.delivery_rate || 0}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-neutral-300">Engagement Score</span>
                          <span className="text-blue-400 font-medium">
                            {selectedWorkflow.performance_metrics?.engagement_score || 0}%
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-neutral-400 mb-1">Emails Sent</label>
                      <p className="text-2xl font-bold text-blue-400">{selectedWorkflow.emails_sent}</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-neutral-700">
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowWorkflowDetails(false)}
                      className="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition-colors"
                    >
                      Close
                    </button>
                    <button className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors">
                      View Full Report
                    </button>
                    <button className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors">
                      Rerun Workflow
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RevolutionaryConversationView;
